import { React, useState, useEffect, useMemo } from "react";
import "animate.css";
import {
	Box,
	Button,
	Typography,
	List,
	Grid,
	Container,
	useMediaQuery,
	useTheme,
} from "@mui/material";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import isEqual from "lodash.isequal";
import { GenerateMealItem, GenerateWeekDays } from "./mealPlannerPresenter";
import GenerateMealPlanDialog from "./generateMealPlanDialog";
import TransitionAlerts from "./transitionAlert";
import BubbleChartIcon from "@mui/icons-material/BubbleChart";

function MealPlanner(props) {
	const now = new Date();
	const week = now.getWeek();
	const year = now.getFullYear();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const [selectedWeek, setSelectedWeek] = useState({
		week: now.getWeek(),
		year: now.getFullYear(),
	});
	//const [title, setTitle] = useState("");
	//const [, setLoading] = useState(false);
	const [mealList, setMealList] = useState(() => {
		const savedPlans = JSON.parse(localStorage.getItem("mealPlans") || "{}");
		const key = selectedWeek.week.toString() + selectedWeek.year.toString();
		return (
			savedPlans[key] || [{}, {}, {}, { title: "Nothing saved" }, {}, {}, {}]
		);
	});
	const [openDialog, setOpenDialog] = useState(false);
	const [openAlert, setOpenAlert] = useState(false);
	const [loading, setLoading] = useState(false);
	//const [isSaved, setIsSaved] = useState(true);
	const [loadingSavedPlans, setLoadingSavedPlans] = useState(false);

	useEffect(() => {
		const savedPlans = JSON.parse(localStorage.getItem("mealPlans") || "{}");
		const key = `${selectedWeek.year}-W${selectedWeek.week}`;
		const saved = savedPlans[key] || [
			{},
			{},
			{},
			{ title: "Nothing saved" },
			{},
			{},
			{},
		];
		setMealList(saved);
	}, [selectedWeek]);

	function handleOpenDialog() {
		setOpenDialog(true);
	}
	function handleCloseDialog() {
		setOpenDialog(false);
	}

	function handleOpenAlert() {
		setOpenAlert(true);
	}
	function handleCloseAlert() {
		setOpenAlert(false);
	}
	function handleMealPlanCreated(mealPlan) {
		setMealList(mealPlan);
	}

	function replaceMeal(index, newMeal) {
		const newMealList = [...mealList];
		newMealList[index] = newMeal;
		setMealList(newMealList);
	}

	const isSaved = useMemo(() => {
		// Get all saved plans
		const savedPlans = JSON.parse(localStorage.getItem("mealPlans") || "{}");
		const key = `${selectedWeek.year}-W${selectedWeek.week}`;

		// Get the saved plan for this week
		const saved = savedPlans[key];

		// If nothing is saved for this week, it's obviously not saved
		if (!saved || loading) return false;

		// Compare the current mealList with the saved plan
		return isEqual(mealList, saved);
	}, [mealList, selectedWeek, loading]);

	/*const getFullWeekCB = async () => {
		setLoading(true);
		try {
			const res = await fetch(
				`https://us-central1-mealplanner-f6b88.cloudfunctions.net/api/random-full-week`
			);
			const data = await res.json();
			if (res.ok) {
				setMealList(data);
				//console.log("Random Week:", data);
			}
		} catch (err) {
			console.log(err);
		} finally {
			setLoading(false);
		}
	};
*/

	async function saveMealPlanFB() {
		const userId = localStorage.getItem("userEmail"); // backend expects userId
		if (!userId) {
			console.error("No userEmail in localStorage");
			return;
		}

		const weekKey = `${selectedWeek.year}-W${selectedWeek.week}`;

		// 1) Keep rich meals locally (unchanged behavior)
		const existingPlans = JSON.parse(localStorage.getItem("mealPlans") || "{}");
		existingPlans[weekKey] = mealList;
		localStorage.setItem("mealPlans", JSON.stringify(existingPlans));

		// convert meal objects -> array of IDs (skip falsy/missing)
		const meals = (mealList || [])
			.map((m) => (m && m.id ? String(m.id) : null))
			.filter(Boolean);

		try {
			setLoading(true);
			const res = await fetch(
				"https://us-central1-mealplanner-f6b88.cloudfunctions.net/api/save-meal-plan-fb",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ userId, weekKey, meals }),
				}
			);

			const data = await res.json().catch(() => ({}));
			if (res.ok) {
				console.log("Saved meal plan:", data);
				// optionally: show toast, update local state, etc.
			} else {
				console.error("Save failed:", data?.error || res.statusText, data);
			}
		} catch (err) {
			console.error("Network error:", err);
		} finally {
			setLoading(false);
		}
	}

	async function saveMealPlan() {
		const email = localStorage.getItem("userEmail");
		if (!email) {
			console.error("No userEmail in localStorage");
			return;
		}

		const weekCode = selectedWeek.week.toString() + selectedWeek.year.toString();

		// 1) Keep rich meals locally (unchanged behavior)
		const existingPlans = JSON.parse(localStorage.getItem("mealPlans") || "{}");
		existingPlans[weekCode] = mealList;
		localStorage.setItem("mealPlans", JSON.stringify(existingPlans));

		// 2) Send only IDs to the backend (what it expects)
		const mealIds = mealList.map((m) => m?.id).filter(Boolean); // skip empty days
		const payload = { email, mealPlan: [weekCode, mealIds] };

		try {
			const res = await fetch(
				"https://us-central1-mealplanner-f6b88.cloudfunctions.net/api/save-meal-plan",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
				}
			);

			const data = await res.json().catch(() => ({}));

			if (res.ok) {
				handleOpenAlert(); // success alert
			} else {
				console.error("Failed to save meal plans:", data?.error || res.statusText);
			}
		} catch (err) {
			console.error("Network error saving meal plan:", err);
		}
	}

	/*const handleSave = async () => {
		try {
			const res = await fetch(
				"https://us-central1-mealplanner-f6b88.cloudfunctions.net/api/save-full-notion-response"
			);

			const data = await res.json();
			if (res.ok) {
				alert(`✅ Saved Notion data: ${data.entries} entries`);
			} else {
				alert("❌ Failed: " + data.error);
			}
		} catch (err) {
			console.error(err);
			alert("Error saving Notion data");
		}
	};
*/
	useEffect(() => {
		const fetchMealPlan = async () => {
			const email = localStorage.getItem("userEmail");
			if (!email) return;

			// Only fetch if nothing in localStorage
			if (!JSON.parse(localStorage.getItem("mealPlans"))) {
				try {
					setLoadingSavedPlans(true);
					const res = await fetch(
						"https://us-central1-mealplanner-f6b88.cloudfunctions.net/api/get-all-meal-plans-from-firestore",
						{
							method: "POST",
							headers: { "Content-Type": "application/json" },
							body: JSON.stringify({ email, expand: true }), // or { email, expand: true } if you want full objects
						}
					);

					const data = await res.json();
					if (!res.ok) {
						console.warn("Server error:", data?.error || res.statusText);
						return;
					}

					// Merge into localStorage
					const existing = JSON.parse(localStorage.getItem("mealPlans") || "{}");
					Object.assign(existing, data.mealPlans);
					localStorage.setItem("mealPlans", JSON.stringify(existing));

					// (optional) set current week in your component
					const key = `${selectedWeek.year}-W${selectedWeek.week}`;
					setMealList(
						existing[key] || [{}, {}, {}, { title: "Nothing saved" }, {}, {}, {}]
					);
				} catch (err) {
					console.error("Failed to fetch meal plan", err);
				} finally {
					setLoadingSavedPlans(false);
				}
			}
		};

		fetchMealPlan();
	}, [week, year]);

	return (
		<div className="Landing animate__animated animate__fadeIn animate__slow">
			<Container
				sx={{
					display: "flex",
					flexDirection: "column", // stack children vertically
					justifyContent: "center", // vertical centering
					alignItems: "center", // horizontal centering
					height: "100vh", // full viewport height (or any height you want)
					textAlign: "center",
				}}
			>
				<Box
					className="Landing-heading"
					sx={{
						textAlign: { xs: "center", md: "center" },
						paddingTop: { xs: 5 },
					}}
				>
					<Typography
						variant="h2"
						className="colorText"
						sx={{
							fontSize: { xs: "2rem", md: "3rem" },
							fontWeight: "bold",
							color: "#333",
							mb: 2,
						}}
					>
						<span className={isMobile ? "colorText" : "colorText"}>Meal</span> Planner
					</Typography>
					<Button variant="contained" onClick={handleOpenDialog}>
						Create New
					</Button>
				</Box>
				<Box sx={{ mb: 2 }}>
					<Button
						onClick={() => {
							const prevDate = new Date(
								selectedWeek.year,
								0,
								1 + (selectedWeek.week - 2) * 7
							);
							setSelectedWeek({
								week: prevDate.getWeek(),
								year: prevDate.getFullYear(),
							});
						}}
					>
						<KeyboardArrowLeftIcon />
					</Button>
					<Typography
						onClick={() => {
							setSelectedWeek({
								week: new Date().getWeek(),
								year: new Date().getFullYear(),
							});
						}}
						variant="overline"
						component="span"
						sx={{ mx: 2 }}
					>
						Week {selectedWeek.week}, {selectedWeek.year}
					</Typography>
					<Button
						onClick={() => {
							const nextDate = new Date(
								selectedWeek.year,
								0,
								1 + selectedWeek.week * 7
							);
							setSelectedWeek({
								week: nextDate.getWeek(),
								year: nextDate.getFullYear(),
							});
						}}
					>
						<KeyboardArrowRightIcon />
					</Button>
				</Box>
				{loadingSavedPlans ? (
					<>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								height: "300px",
								width: "300px",
								justifyContent: "center", // vertical centering
								alignItems: "center",
								//backgroundColor: green[200],
								gap: 2,
							}}
						>
							<BubbleChartIcon
								className={
									loadingSavedPlans
										? "animate__animated animate__wobble animate__infinite"
										: ""
								}
								sx={{ fontSize: "5em" }}
							/>
							{loading ? (
								""
							) : (
								<Typography variant="overline">Loading your saved plans...</Typography>
							)}
						</Box>
					</>
				) : (
					<>
						<Grid
							container
							spacing={{ xs: 1, md: 2 }}
							justifyContent={{ xs: "center", md: "center" }}
							wrap="wrap"
							alignItems="flex-start"
							maxWidth={isMobile ? "90%" : "50%"}
						>
							<Grid item sx={{ maxWidth: "30%", textAlign: "center" }}>
								<GenerateWeekDays selectedWeek={selectedWeek} />
							</Grid>
							<Grid item sx={{ width: "68%", textAlign: "center" }}>
								<List>
									{mealList.map((item, i) => (
										<GenerateMealItem item={item} index={i} replaceMeal={replaceMeal} />
									))}
								</List>
							</Grid>
						</Grid>
						<Box sx={{ p: 1 }} />
						<Button
							variant="contained"
							disabled={
								isSaved ||
								JSON.stringify(mealList) ===
									JSON.stringify([{}, {}, {}, { title: "Nothing saved" }, {}, {}, {}])
							}
							onClick={() => {
								//saveMealPlan();
								saveMealPlanFB();
							}}
						>
							{loading
								? "Saving..."
								: isSaved
								? "This meal plan is saved"
								: "Save meal plan"}
						</Button>
					</>
				)}

				<TransitionAlerts
					text={"Save successful"}
					openAlert={openAlert}
					onClose={handleCloseAlert}
				/>
			</Container>
			<GenerateMealPlanDialog
				openStatus={openDialog}
				onClose={handleCloseDialog}
				onMealPlanCreated={handleMealPlanCreated}
			/>
		</div>
	);
}

export default MealPlanner;
